import { GlobalSearchBar } from "@/app/global_searchbar.tsx"
import { accounting_pages_hidden } from "@/app/page_access"
import { UserState } from "@/auth/user"
import { ActivityFeed } from "@/components/activity-feed"
import { Button } from "@/components/ui/button"
import { DateRangeFilter, CustomerFilter, DateKindToggle } from "@/global_filter"
import { useGlobalFilterUrlSync } from "@/global_filter/use_global_filter_url_sync.ts"
import { loadOrgFieldConfigs } from "@/org_config/state.tsx"
import { useOrgSettings } from "@/org_settings/state"
import { Bell, Wand2 } from "lucide-react"
import { useEffect, useState } from "preact/hooks"
import { Link, useLocation } from "wouter-preact"
import { openBulkWizard } from "@/deduction/bulk_wizard_state"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface NavbarProps {
  children?: React.ReactNode
}

interface IconButtonWithTooltipProps {
  tooltip: string
  onClick: () => void
  icon: React.ReactNode
  className?: string
}

function IconButtonWithTooltip({ tooltip, onClick, icon, className }: IconButtonWithTooltipProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false)

  return (
    <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          // @ts-ignore
          size="icon"
          onClick={onClick}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(true)}
          onBlur={() => setTooltipOpen(false)}
          class={className}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  )
}

interface GlobalFiltersProps {
  /**
   * False on a page with its own customer select (Cash Application filters by the customer each
   * check names), so the page does not show two customer pickers that disagree.
   */
  show_customer_filter?: boolean
}

export function GlobalFilters({ show_customer_filter = true }: GlobalFiltersProps = {}) {
  const [showActivityFeed, setShowActivityFeed] = useState(false)
  const [location] = useLocation()
  const isDeductionsPage = location === "/deductions"

  useGlobalFilterUrlSync()

  useEffect(() => {
    void loadOrgFieldConfigs()
  }, [])

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-4 gap-y-2 ml-auto">
      {show_customer_filter && <CustomerFilter />}
      <DateKindToggle />
      <DateRangeFilter />
      <TooltipProvider>
        {isDeductionsPage && (
          <IconButtonWithTooltip
            tooltip="Bulk Wizard"
            onClick={() => openBulkWizard()}
            icon={<Wand2 className="h-5 w-5" />}
            className="text-purple-600 border-purple-200 hover:bg-purple-50 hover:border-purple-300"
          />
        )}
        <IconButtonWithTooltip
          tooltip="Activity Feed"
          onClick={() => setShowActivityFeed(true)}
          icon={<Bell className="h-5 w-5" />}
        />
      </TooltipProvider>
      <ActivityFeed open={showActivityFeed} onOpenChange={setShowActivityFeed} />
    </div>
  )
}

export function DeductionNavbar({ children }: NavbarProps) {
  return (
    <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <GlobalSearchBar />
      {children}
    </div>
  )
}

export function CheckNavbar({ children }: NavbarProps) {
  return (
    <div class="flex justify-between pb-12 space-x-2 items-center">
      <GlobalSearchBar />
      {children}
    </div>
  )
}

interface ToggleViewButtonProps {
  deductionId: string
  currentPath: string
}

const createToggleUrl = (currentPath: string, deductionId: string): string => {
  const isDeductionView = currentPath.includes("/deduction/")
  return isDeductionView ? `/accounting/transaction/${deductionId}` : `/deduction/${deductionId}`
}

const getToggleButtonText = (currentPath: string): string => {
  return currentPath.includes("/deduction/") ? "Accounting View" : "Deduction View"
}

export function ToggleViewButton({ deductionId, currentPath }: ToggleViewButtonProps) {
  const toggleUrl = createToggleUrl(currentPath, deductionId)
  const buttonText = getToggleButtonText(currentPath)

  return (
    <Link href={toggleUrl}>
      <Button variant="outline" className="space-x-2">
        <span>{buttonText}</span>
      </Button>
    </Link>
  )
}

export function DetailNavbar() {
  const currentPath = window.location.pathname
  const deductionId = currentPath.split("/").pop() || ""
  const isCheckPage = currentPath.includes("/accounting/check/")
  const hide_accounting = accounting_pages_hidden(UserState.use(), useOrgSettings())

  return (
    <div className="flex justify-between items-center mx-4">
      <GlobalSearchBar />
      {!isCheckPage && !hide_accounting && <ToggleViewButton deductionId={deductionId} currentPath={currentPath} />}
    </div>
  )
}
